import { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  FormControl,
  TextField,
  InputAdornment,
  Stack,
  Button,
  capitalize
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchTwoToneIcon from '@mui/icons-material/SearchTwoTone';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useTheme } from '@mui/material/styles';
import { IProject } from 'src/models/users';
import { useFiltersByName } from 'src/hooks/useFiltersByName';
import { useUnlimitedScroll } from 'src/hooks/useUnlimitedScroll';
import Empty from 'src/components/Empty';

interface Item {
  name: string;
}

interface IProjectListDialogProps {
  open: boolean;
  onClose: () => void;
  project: IProject;
  projectList: Array<{ id: string; displayName: string }>;
}

const ProjectListDialog = ({
  open,
  onClose,
  project,
  projectList
}: IProjectListDialogProps) => {
  const theme = useTheme();

  const addName = useMemo((): Array<IProject & Item> => {
    if (!projectList) return [];

    return projectList.map((object) => ({
      ...object,
      name: object.displayName
    })) as Array<IProject & Item>;
  }, [projectList]);

  const { filteredList, handleFilter } = useFiltersByName<IProject>(addName);
  const { fetchMoreData, infiniteScroll } =
    useUnlimitedScroll<IProject>(filteredList);

  const handleChangeProject = (projectNew: IProject) => {
    window.localStorage.setItem('project', projectNew.id);
    window.location.assign('/');
  };

  return (
    <Dialog onClose={onClose} open={open} fullWidth maxWidth="sm">
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500]
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogTitle
        sx={{
          m: theme.spacing(5, 3, 0),
          p: theme.spacing(2, 2, 0)
        }}
      >
        <Typography fontWeight={600} fontSize={20}>
          Search Your Project
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ m: theme.spacing(1, 3) }}>
        <FormControl fullWidth size="medium">
          <TextField
            sx={{ mt: 2 }}
            size="small"
            fullWidth
            placeholder="Search project"
            onChange={(e) => handleFilter(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchTwoToneIcon
                    sx={{ color: theme.palette.primary.main }}
                  />
                </InputAdornment>
              )
            }}
          />
        </FormControl>

        <Stack>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: 13,
              color: theme.palette.primary.dark,
              m: theme.spacing(4, 0, 1, 0)
            }}
          >
            All Project
          </Typography>

          <InfiniteScroll
            dataLength={infiniteScroll.list.length}
            next={fetchMoreData}
            hasMore={infiniteScroll.hasMore}
            loader={<div />}
            height={288}
            endMessage={<div />}
          >
            {infiniteScroll.list.length > 0 ? (
              infiniteScroll.list.map((row) => {
                const isActive = row.id === project.id;

                return (
                  <div key={row.id}>
                    <Button
                      disabled={isActive}
                      onClick={() => !isActive && handleChangeProject(row)}
                      sx={{
                        borderRadius: 1,
                        margin: theme.spacing(0, 0, 1.5, 1),
                        ...(isActive && {
                          background: '#C1ECFE'
                        })
                      }}
                    >
                      <Typography
                        fontWeight={600}
                        fontSize={14}
                        color={theme.palette.primary.main}
                      >
                        {capitalize(row.displayName)}
                      </Typography>
                    </Button>
                  </div>
                );
              })
            ) : (
              <Stack
                sx={{
                  height: 'calc(80vh - 400px)',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                <Empty />
              </Stack>
            )}
          </InfiniteScroll>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectListDialog;
